import { createContext, createElement, useContext, type ReactNode } from "react";
import type { KnowledgeViewKey } from "../knowledge/types";
import { ALL_ENABLED, type EntitlementsStatus } from "./entitlements";
import type { Entitlements, FeatureKey } from "./types";

interface EntitlementsContextValue {
  readonly entitlements: Entitlements;
  readonly status: EntitlementsStatus;
}

const EntitlementsCtx = createContext<EntitlementsContextValue>({
  entitlements: ALL_ENABLED,
  status: "ambient",
});

export interface EntitlementsProviderProps {
  /** The resolved capability set. Defaults to all-enabled. */
  readonly value?: Entitlements;
  /**
   * How `value` was arrived at. Defaults to `"ambient"` — the build's own
   * default, which is what the public preview provides and what a console
   * provides outside a workspace.
   */
  readonly status?: EntitlementsStatus;
  readonly children: ReactNode;
}

/**
 * Provides the active {@link Entitlements} to the tree, together with how they
 * were resolved.
 *
 * The status rides along because a closed capability set means two different
 * things — "not in your plan" and "we have not been told yet" — and a surface
 * that can only see the booleans has to guess which. Guessing produced the
 * failure this replaced: a still-resolving plan was rendered as an unlimited one.
 */
export function EntitlementsProvider({ value, status, children }: EntitlementsProviderProps) {
  return createElement(
    EntitlementsCtx.Provider,
    { value: { entitlements: value ?? ALL_ENABLED, status: status ?? "ambient" } },
    children,
  );
}

export function useEntitlements(): Entitlements {
  return useContext(EntitlementsCtx).entitlements;
}

/**
 * How the active capabilities were resolved.
 *
 * Read this before telling a reader a capability is unavailable: while the
 * status is `"loading"` every answer is closed but nothing has been denied, and
 * on `"error"` the honest statement is that the plan could not be read.
 */
export function useEntitlementsStatus(): EntitlementsStatus {
  return useContext(EntitlementsCtx).status;
}

export function useFeature(feature: FeatureKey): boolean {
  return useContext(EntitlementsCtx).entitlements.isFeatureEnabled(feature);
}

export function useViewEnabled(view: KnowledgeViewKey): boolean {
  return useContext(EntitlementsCtx).entitlements.isViewEnabled(view);
}
